import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import RecordMetadata from '@/components/RecordMetadata';
import StatusDeleteConfirmDialog from './StatusDeleteConfirmDialog';
import { Trash2 } from 'lucide-react';
import { generateUniqueName } from '@prolarus/shared';
import { useAuth } from '@/contexts/AuthContext';
import { statusService, statusWorkflowService, type ServiceContext, type StatusEntityType } from '@/services';

const statusSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  unique_name: z.string()
    .min(1, 'Unique name is required')
    .max(500, 'Unique name must be 500 characters or less')
    .regex(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, and underscores allowed'),
  seq_num: z.coerce.number().int().min(1, 'Sequence must be at least 1'),
  subtext: z.string().max(200).optional().or(z.literal('')),
  tooltip: z.string().max(500).optional().or(z.literal('')),
  readonly: z.boolean().default(false),
});

type StatusFormValues = z.infer<typeof statusSchema>;

interface StatusFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status?: {
    id: string;
    name: string;
    unique_name: string;
    seq_num: number;
    subtext: string | null;
    tooltip: string | null;
    readonly: boolean | null;
    created: string;
    created_by: string | null;
    modified: string;
    updated_by: string | null;
    created_by_profile?: { full_name: string | null } | null;
    updated_by_profile?: { full_name: string | null } | null;
  } | null;
  entityType: string;
  companyId: string;
  nextSeqNum: number;
  availableStatuses: Array<{ id: string; name: string; seq_num: number }>;
}

export default function StatusFormDialog({
  open,
  onOpenChange,
  status,
  entityType,
  companyId,
  nextSeqNum,
  availableStatuses,
}: StatusFormDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!status;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const userEditedUniqueName = useRef(false);

  // Filter out the current status from available statuses for reassignment
  const reassignmentStatuses = availableStatuses.filter(s => s.id !== status?.id);

  const form = useForm<StatusFormValues>({
    resolver: zodResolver(statusSchema),
    defaultValues: {
      name: '',
      unique_name: '',
      seq_num: nextSeqNum,
      subtext: '',
      tooltip: '',
      readonly: false,
    },
  });

  // Watch name field for auto-population of unique_name
  const nameValue = form.watch('name');

  useEffect(() => {
    // Only auto-populate unique_name for new statuses when user hasn't manually edited it
    if (!isEditing && !userEditedUniqueName.current) {
      const generated = generateUniqueName(nameValue);
      form.setValue('unique_name', generated);
    }
  }, [nameValue, isEditing, form]);

  useEffect(() => {
    // Reset userEditedUniqueName when dialog opens/closes or status changes
    userEditedUniqueName.current = false;
    
    if (status) {
      form.reset({
        name: status.name,
        unique_name: status.unique_name,
        seq_num: status.seq_num,
        subtext: status.subtext || '',
        tooltip: status.tooltip || '',
        readonly: status.readonly || false,
      });
    } else {
      form.reset({
        name: '',
        unique_name: '',
        seq_num: nextSeqNum,
        subtext: '',
        tooltip: '',
        readonly: false,
      });
    }
  }, [status, nextSeqNum, form]);

  const getServiceContext = (): ServiceContext => ({
    companyId,
    userId: user?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (values: StatusFormValues) => {
      const result = await statusService.create(
        {
          entity_type: entityType,
          name: values.name,
          unique_name: values.unique_name,
          seq_num: values.seq_num,
          subtext: values.subtext || null,
          tooltip: values.tooltip || null,
          readonly: values.readonly,
        },
        getServiceContext()
      );
      if (result.error) {
        if (result.error.message.includes('idx_statuses_unique_name_per_entity')) {
          throw new Error('This unique name is already used by another status for this entity type.');
        }
        throw result.error;
      }
    },
    onSuccess: () => {
      toast.success('Status created successfully');
      queryClient.invalidateQueries({ queryKey: ['statuses', companyId, entityType] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to create status: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: StatusFormValues) => {
      const snapshot = {
        name: status!.name,
        unique_name: status!.unique_name,
        seq_num: status!.seq_num,
        subtext: status!.subtext,
        tooltip: status!.tooltip,
        readonly: status!.readonly,
      };
      
      const result = await statusService.update(
        status!.id,
        {
          name: values.name,
          unique_name: values.unique_name,
          seq_num: values.seq_num,
          subtext: values.subtext || null,
          tooltip: values.tooltip || null,
          readonly: values.readonly,
        },
        snapshot,
        getServiceContext()
      );
      if (result.error) {
        if (result.error.message.includes('idx_statuses_unique_name_per_entity')) {
          throw new Error('This unique name is already used by another status for this entity type.');
        }
        throw result.error;
      }
    },
    onSuccess: () => {
      toast.success('Status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['statuses', companyId, entityType] });
      queryClient.invalidateQueries({ queryKey: ['status_workflows', companyId, entityType] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update status: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (reassignToStatusId?: string) => {
      const context = getServiceContext();
      
      // Step 1: Reassign records if needed (pass context for history logging)
      if (reassignToStatusId && status) {
        const reassignResult = await statusService.reassignRecords(
          entityType as StatusEntityType,
          status.id,
          reassignToStatusId,
          context
        );
        if (reassignResult.error) throw reassignResult.error;
      }

      // Step 2: Delete related workflows
      await statusWorkflowService.deleteByStatus(status!.id, context);

      // Step 3: Delete the status
      const snapshot = {
        name: status!.name,
        unique_name: status!.unique_name,
        entity_type: entityType,
        seq_num: status!.seq_num,
      };
      const result = await statusService.delete(status!.id, snapshot, context);
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      toast.success('Status deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['statuses', companyId, entityType] });
      queryClient.invalidateQueries({ queryKey: ['status_workflows', companyId, entityType] });
      // Also invalidate the entity queries in case records were reassigned
      if (entityType === 'invoice') {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
      } else if (entityType === 'vendor') {
        queryClient.invalidateQueries({ queryKey: ['vendors'] });
      } else if (entityType === 'price_book') {
        queryClient.invalidateQueries({ queryKey: ['price_books'] });
      } else if (entityType === 'ticket') {
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
      }
      setDeleteDialogOpen(false);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete status: ${error.message}`);
    },
  });

  const handleConfirmDelete = (reassignToStatusId?: string) => {
    deleteMutation.mutate(reassignToStatusId);
  };

  const onSubmit = (values: StatusFormValues) => {
    if (isEditing) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Status' : 'Create New Status'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the status details below.' : 'Enter the details for the new status.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter status name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="unique_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unique Name *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="unique_name_for_api" 
                      {...field} 
                      onChange={(e) => {
                        userEditedUniqueName.current = true;
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Used for API integrations. Only lowercase letters, numbers, and underscores.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seq_num"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sequence *</FormLabel>
                  <FormControl>
                    <Input type="number" min={1} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subtext"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subtext</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional secondary text" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tooltip"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tooltip</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional hover tooltip" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="readonly"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Readonly</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            {isEditing && status && (
              <RecordMetadata record={status} />
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              {isEditing && status && (
                <Button
                  type="button"
                  variant="destructive"
                  className="mr-auto"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>

        {/* Delete Confirmation Dialog */}
        {isEditing && status && (
          <StatusDeleteConfirmDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            status={{ id: status.id, name: status.name, seq_num: status.seq_num }}
            entityType={entityType}
            companyId={companyId}
            availableStatuses={reassignmentStatuses}
            onConfirmDelete={handleConfirmDelete}
            isDeleting={deleteMutation.isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
